import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  FileText,
  Lightbulb,
  Package,
  ShieldCheck,
  Target,
  ThumbsUp,
  ThumbsDown,
  Layers,
} from 'lucide-react';
import { fetchPlaybook } from '../lib/api';
import type { Playbook, PlaybookPhase, PlaybookPhaseDeliverable, PromptTemplate } from '../types/wizard';

/* ------------------------------------------------------------------ */
/*  Copy button                                                        */
/* ------------------------------------------------------------------ */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-surface-100 text-text-secondary hover:text-white hover:bg-surface-200 border border-dark-border transition-colors"
    >
      <ClipboardCopy className="w-3.5 h-3.5" />
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Expandable prompt card                                             */
/* ------------------------------------------------------------------ */
function PromptCard({ prompt }: { prompt: PromptTemplate }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-dark-border rounded-lg bg-surface-50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-surface-100 transition-colors"
      >
        <FileText className="w-4 h-4 text-accent mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-white">{prompt.name}</h4>
          <p className="text-xs text-text-tertiary mt-0.5">{prompt.description}</p>
        </div>
        <ChevronDown
          className={clsx(
            'w-4 h-4 text-text-tertiary shrink-0 transition-transform duration-200',
            expanded && 'rotate-180'
          )}
        />
      </button>

      {expanded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="border-t border-dark-border"
        >
          {/* Prompt text */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-text-tertiary uppercase tracking-wider">Prompt</span>
              <CopyButton text={prompt.prompt} />
            </div>
            <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono bg-surface-200 rounded-lg p-4 border border-dark-border leading-relaxed max-h-96 overflow-y-auto">
              {prompt.prompt}
            </pre>
          </div>

          {/* Expected output */}
          {prompt.expectedOutput && (
            <div className="px-4 pb-4">
              <span className="text-xs font-medium text-text-tertiary uppercase tracking-wider">Expected Output</span>
              <p className="text-sm text-gray-400 mt-1">{prompt.expectedOutput}</p>
            </div>
          )}

          {/* Tips */}
          {prompt.tips && prompt.tips.length > 0 && (
            <div className="px-4 pb-4">
              <span className="text-xs font-medium text-text-tertiary uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Lightbulb className="w-3.5 h-3.5 text-yellow-500" />
                Tips
              </span>
              <ul className="space-y-1">
                {prompt.tips.map((tip, i) => (
                  <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
                    <span className="text-yellow-500 mt-0.5">-</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Phase detail panel                                                 */
/* ------------------------------------------------------------------ */
function PhaseDetail({ phase, phaseIndex }: { phase: PlaybookPhase; phaseIndex: number }) {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent/10 text-accent font-bold text-lg border border-accent/20">
            {phaseIndex + 1}
          </span>
          <div>
            <h2 className="text-2xl font-bold text-white">{phase.name}</h2>
          </div>
        </div>
        <p className="text-gray-400 leading-relaxed">{phase.description}</p>
      </div>

      {/* Objectives */}
      {(phase.objectives?.length ?? 0) > 0 && (
        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white uppercase tracking-wider mb-3">
            <Target className="w-4 h-4 text-accent" />
            Objectives
          </h3>
          <ul className="space-y-2">
            {(phase.objectives ?? []).map((obj, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                {obj}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Deliverables */}
      {(phase.deliverables?.length ?? 0) > 0 && (
        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white uppercase tracking-wider mb-3">
            <Package className="w-4 h-4 text-accent" />
            Deliverables
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {(phase.deliverables ?? []).map((d, i) => {
              const isStructured = typeof d === 'object';
              const deliverable = isStructured ? (d as PlaybookPhaseDeliverable) : null;
              return (
                <div
                  key={i}
                  className="rounded-lg border border-dark-border bg-surface-50 p-3"
                >
                  <p className="text-sm font-medium text-white">
                    {isStructured ? deliverable!.name : (d as string)}
                  </p>
                  {isStructured && deliverable!.description && (
                    <p className="text-xs text-text-tertiary mt-1">{deliverable!.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Quality Gates */}
      {(phase.qualityGates?.length ?? 0) > 0 && (
        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white uppercase tracking-wider mb-3">
            <ShieldCheck className="w-4 h-4 text-accent" />
            Quality Gates
          </h3>
          <ul className="space-y-2">
            {(phase.qualityGates ?? []).map((gate, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                <div className="w-4 h-4 rounded border border-dark-border bg-surface-100 mt-0.5 shrink-0 flex items-center justify-center">
                  <Check className="w-3 h-3 text-text-tertiary" />
                </div>
                {gate}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Claude Instructions */}
      {phase.claudeInstructions && (
        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white uppercase tracking-wider mb-3">
            Claude Instructions
          </h3>
          <div className="rounded-lg border border-accent/20 bg-accent/5 p-4">
            <p className="text-sm text-gray-300 leading-relaxed">{phase.claudeInstructions}</p>
          </div>
        </section>
      )}

      {/* Claude DO / DON'T */}
      {((phase.claudeDo && phase.claudeDo.length > 0) || (phase.claudeDoNot && phase.claudeDoNot.length > 0)) && (
        <section>
          <div className="grid sm:grid-cols-2 gap-4">
            {/* DO */}
            {phase.claudeDo && phase.claudeDo.length > 0 && (
              <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-4">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-green-400 mb-3">
                  <ThumbsUp className="w-4 h-4" />
                  DO
                </h4>
                <ul className="space-y-2">
                  {phase.claudeDo.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <span className="text-green-400 mt-0.5 shrink-0">+</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* DON'T */}
            {phase.claudeDoNot && phase.claudeDoNot.length > 0 && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-red-400 mb-3">
                  <ThumbsDown className="w-4 h-4" />
                  DON'T
                </h4>
                <ul className="space-y-2">
                  {phase.claudeDoNot.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <span className="text-red-400 mt-0.5 shrink-0">-</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Prompt Templates */}
      {(phase.promptTemplates?.length ?? 0) > 0 && (
        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white uppercase tracking-wider mb-3">
            <FileText className="w-4 h-4 text-accent" />
            Prompt Templates ({(phase.promptTemplates?.length ?? 0)})
          </h3>
          <div className="space-y-3">
            {(phase.promptTemplates ?? []).map((pt) => (
              <PromptCard key={pt.id} prompt={pt} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */
export default function PlaybookDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [playbook, setPlaybook] = useState<Playbook | null>(null);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = useState(0);
  const [mobileSelectorOpen, setMobileSelectorOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchPlaybook(id)
      .then((data) => setPlaybook(data))
      .catch(() => setPlaybook(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-20 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-dark-border border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  if (!playbook) {
    return (
      <div className="min-h-screen pt-28 pb-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Playbook not found</h1>
          <Link to="/playbooks" className="text-accent hover:text-accent-400">
            Back to Playbooks
          </Link>
        </div>
      </div>
    );
  }

  const hasRichContent = playbook.phases.length > 0 && playbook.phases.some((p) => (p.objectives?.length ?? 0) > 2);
  const currentPhase = playbook.phases[activePhase];

  return (
    <div className="relative min-h-screen pt-28 pb-20">
      {/* Background */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-20" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-accent/5 rounded-full blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          <Link
            to="/playbooks"
            className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All Playbooks
          </Link>
        </motion.div>

        {/* Playbook header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <div className="flex items-center gap-4 mb-3">
            <span className="text-4xl">{playbook.icon}</span>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">{playbook.name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm text-text-tertiary font-mono">{playbook.projectType}</span>
                <span className="flex items-center gap-1 text-xs text-text-tertiary bg-surface-100 px-2 py-0.5 rounded-full border border-dark-border">
                  <Layers className="w-3 h-3" />
                  {playbook.phases.length} phases
                </span>
              </div>
            </div>
          </div>
          <p className="text-gray-400 max-w-3xl">{playbook.description}</p>
        </motion.div>

        {!hasRichContent ? (
          /* Empty state for minimal playbooks */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-center py-20"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-surface-100 border border-dark-border mb-4">
              <Layers className="w-8 h-8 text-text-tertiary" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Rich content coming soon</h2>
            <p className="text-gray-400 max-w-md mx-auto mb-6">
              This playbook has {playbook.phases.length} phases defined. Full prompt templates, deliverables,
              and Claude instructions will be added in a future update.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {playbook.phases.map((phase, i) => (
                <span key={phase.id} className="px-3 py-1.5 text-sm rounded-lg bg-surface-100 text-gray-400 border border-dark-border">
                  {i + 1}. {phase.name}
                </span>
              ))}
            </div>
          </motion.div>
        ) : (
          /* Two-column layout */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-col lg:flex-row gap-8"
          >
            {/* Mobile phase selector */}
            <div className="lg:hidden">
              <button
                onClick={() => setMobileSelectorOpen(!mobileSelectorOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-surface-100 border border-dark-border text-white"
              >
                <span className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded bg-accent/10 text-accent text-xs font-bold">
                    {activePhase + 1}
                  </span>
                  {currentPhase?.name}
                </span>
                <ChevronDown className={clsx('w-4 h-4 transition-transform', mobileSelectorOpen && 'rotate-180')} />
              </button>
              {mobileSelectorOpen && (
                <div className="mt-2 rounded-lg bg-surface-100 border border-dark-border overflow-hidden">
                  {playbook.phases.map((phase, i) => (
                    <button
                      key={phase.id}
                      onClick={() => {
                        setActivePhase(i);
                        setMobileSelectorOpen(false);
                      }}
                      className={clsx(
                        'w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors',
                        i === activePhase
                          ? 'bg-accent/10 text-white'
                          : 'text-text-secondary hover:bg-surface-200 hover:text-white'
                      )}
                    >
                      <span
                        className={clsx(
                          'flex items-center justify-center w-6 h-6 rounded text-xs font-bold',
                          i === activePhase
                            ? 'bg-accent text-white'
                            : 'bg-surface-200 text-text-tertiary'
                        )}
                      >
                        {i + 1}
                      </span>
                      {phase.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar - phase timeline */}
            <div className="hidden lg:block w-72 shrink-0">
              <div className="sticky top-28">
                <h3 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-3 px-2">
                  Phases
                </h3>
                <nav className="space-y-1">
                  {playbook.phases.map((phase, i) => (
                    <button
                      key={phase.id}
                      onClick={() => setActivePhase(i)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-all',
                        i === activePhase
                          ? 'bg-accent/10 text-white border border-accent/20'
                          : 'text-text-secondary hover:bg-surface-100 hover:text-white border border-transparent'
                      )}
                    >
                      <span
                        className={clsx(
                          'flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold shrink-0 transition-colors',
                          i === activePhase
                            ? 'bg-accent text-white'
                            : 'bg-surface-200 text-text-tertiary'
                        )}
                      >
                        {i + 1}
                      </span>
                      <span className="truncate">{phase.name}</span>
                      {i === activePhase && (
                        <ChevronRight className="w-3.5 h-3.5 ml-auto text-accent shrink-0" />
                      )}
                    </button>
                  ))}
                </nav>

                {/* Phase stats */}
                <div className="mt-6 px-2">
                  <div className="rounded-lg bg-surface-100 border border-dark-border p-3 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-text-tertiary">Prompts</span>
                      <span className="text-white font-medium">
                        {currentPhase?.promptTemplates?.length ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-text-tertiary">Deliverables</span>
                      <span className="text-white font-medium">
                        {currentPhase?.deliverables?.length ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-text-tertiary">Quality Gates</span>
                      <span className="text-white font-medium">
                        {currentPhase?.qualityGates?.length ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              <div className="card p-6 sm:p-8">
                {currentPhase && (
                  <PhaseDetail phase={currentPhase} phaseIndex={activePhase} />
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
